import { useEffect, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Box, DateField, SelectNative, TextField } from '@alto-avios/alto-ui';
import { useTranslation } from '@/utils/useTranslation';
import {
  isSelectOptionValid,
  isAgeValid,
  isCityValid,
  isPostcodeValid,
  isStreetAddressValid,
  isNameValid,
} from '@/utils/validations';
import { SectionHeading } from '@/components/SectionHeading';
import { getCountriesOptions } from '@/utils/countries';
import { useGlobalContext } from '@/components/GlobalProvider';

//Note: we would still allow the following symbols: _, -, & and .
const REGEX_CONTAINS_NUMBERS_AND_SYMBOLS = /[\d!£@#$%^*(),?":{}|<>+=/\\]/;
const REGEX_DISALLOWED_NAME_CHARACTERS =
  /[!@#$^&*(),.?":{}|<>'[\]`~+=_;%£€¥§±•∑®†¡¢∞¶ªº–≠‘πæ«÷≥≤µ∫√≈ƒ©˙∆˚¬…¨\\/]/g;

const sanitizeNameInput = (value: string) => {
  return value.replaceAll(REGEX_DISALLOWED_NAME_CHARACTERS, '');
};

export const PersonalDetails = () => {
  const {
    formState: { errors },
    watch,
    setValue,
    control,
  } = useFormContext();
  const { t } = useTranslation('personalDetails');
  const { programmeId } = useGlobalContext();

  const [firstName, middleName, lastName] = watch([
    'firstName',
    'middleName',
    'lastName',
  ]);

  useEffect(() => {
    // When the first name and last name values change, we will
    // confirm if the user typed a number or a special character.
    // If he did, we will remove that character as we do not allow numbers and special characters
    // in these 2 fields.
    if (REGEX_CONTAINS_NUMBERS_AND_SYMBOLS.test(firstName)) {
      setValue('firstName', sanitizeNameInput(firstName));
    }

    if (middleName && REGEX_CONTAINS_NUMBERS_AND_SYMBOLS.test(middleName)) {
      setValue('middleName', sanitizeNameInput(middleName));
    }

    if (REGEX_CONTAINS_NUMBERS_AND_SYMBOLS.test(lastName)) {
      setValue('lastName', sanitizeNameInput(lastName));
    }
  }, [firstName, middleName, lastName, setValue]);

  const countriesOptions = useMemo(
    () => getCountriesOptions(programmeId),
    [programmeId],
  );

  return (
    <Box display="flex" flexDirection="column" gap="sm">
      <SectionHeading title={t('heading')} />

      <Controller
        name="firstName"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (value: string) => {
            const result = isNameValid(value);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'EMPTY':
                  return t('fieldRequired');
                case 'TOO_LONG':
                  return t('firstName.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return t('firstName.error.invalidCharacters');
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            label={t('firstName.label')}
            description={t('firstName.description')}
            data-testid="firstName"
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['firstName']}
            errorMessage={errors['firstName']?.message as string}
          />
        )}
      />

      <Controller
        name="middleName"
        control={control}
        defaultValue=""
        rules={{
          validate: (value: string) => {
            if (!value) {
              return true;
            }

            const result = isNameValid(value);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'TOO_LONG':
                  return t('middleName.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return t('middleName.error.invalidCharacters');
                case 'EMPTY':
                  return true;
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            label={t('middleName.label')}
            description={t('middleName.description')}
            data-testid="middleName"
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['middleName']}
            errorMessage={errors['middleName']?.message as string}
          />
        )}
      />

      <Controller
        name="lastName"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (value: string) => {
            const result = isNameValid(value);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'EMPTY':
                  return t('fieldRequired');
                case 'TOO_LONG':
                  return t('lastName.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return t('lastName.error.invalidCharacters');
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            label={t('lastName.label')}
            description={t('lastName.description')}
            data-testid="lastName"
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['lastName']}
            errorMessage={errors['lastName']?.message as string}
          />
        )}
      />

      <Controller
        name="dateOfBirth"
        control={control}
        defaultValue={null}
        rules={{
          required: t('fieldRequired'),
          validate: (input: Date) => {
            const result = isAgeValid(input);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'TOO_YOUNG':
                  return t('dateOfBirth.error.tooYoung');
                case 'TOO_OLD':
                  return t('dateOfBirth.error.tooOld');
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <DateField
            id="dateOfBirth"
            data-testid="dateOfBirth-input"
            label={t('dateOfBirth.label')}
            description={t('dateOfBirth.description')}
            isRequired
            value={field.value ?? null}
            onChange={field.onChange}
            isInvalid={!!errors['dateOfBirth']}
            errorMessage={errors['dateOfBirth']?.message as string}
          />
        )}
      />

      {countriesOptions && (
        <Controller
          name="countryOfResidence"
          control={control}
          defaultValue=""
          rules={{
            required: t('fieldRequired'),
            validate: (country: string) => {
              if (!isSelectOptionValid(country)) return t('fieldRequired');
            },
          }}
          render={({ field }) => (
            <SelectNative
              id="countryOfResidence"
              label={t('countryOfResidence.label')}
              data-testid="countryOfResidence"
              placeholder={t('countryOfResidence.placeholder')}
              onChange={field.onChange}
              errorMessage={errors['countryOfResidence']?.message as string}
              isInvalid={!!errors['countryOfResidence']}
              options={countriesOptions}
              isRequired
            />
          )}
        />
      )}

      <Controller
        name="addressLine"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (value: string) => {
            const result = isStreetAddressValid(value);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'EMPTY':
                  return t('fieldRequired');
                case 'TOO_LONG':
                  return t('addressLine.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return `${t('addressLine.error.invalidCharacters')}: ${error.characters.join(', ')}`;
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            label={t('addressLine.label')}
            isRequired
            data-testid="streetAddress"
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['addressLine']}
            errorMessage={errors['addressLine']?.message as string}
          />
        )}
      />

      <Controller
        name="city"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (value: string) => {
            const result = isCityValid(value);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'EMPTY':
                  return t('fieldRequired');
                case 'TOO_LONG':
                  return t('city.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return `${t('city.error.invalidCharacters')}: ${error.characters.join(', ')}`;
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            data-testid="city"
            label={t('city.label')}
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['city']}
            errorMessage={errors['city']?.message as string}
          />
        )}
      />

      <Controller
        name="postCode"
        control={control}
        defaultValue=""
        rules={{
          required: t('fieldRequired'),
          validate: (postCode: string) => {
            const result = isPostcodeValid(postCode);

            if (!result.isValid) {
              const { error } = result;
              switch (error.type) {
                case 'EMPTY':
                  return t('fieldRequired');
                case 'TOO_LONG':
                  return t('postCode.error.tooLong');
                case 'INVALID_CHARACTERS':
                  return `${t('postCode.error.invalidCharacters')}: ${error.characters.join(', ')}`;
              }
            }

            return true;
          },
        }}
        render={({ field }) => (
          <TextField
            label={t('postCode.label')}
            data-testid="postCode"
            isRequired
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!errors['postCode']}
            errorMessage={errors['postCode']?.message as string}
          />
        )}
      />
    </Box>
  );
};
